import React from 'react';
import { useTranslation } from 'react-i18next';
import Modal from './Modal';
import {
  cumulativeWorkedMinutes,
  currentSessionMinutes,
  fromDateTimeLocalValue,
  parsePauseLunchMinutes,
  sessionEndFromMinutes,
  sessionNetMinutes,
  toDateTimeLocalValue,
  totalWorkedMinutes
} from '../utils/taskCheckInTime';

export function TaskNotesCard({ checkOutData, setCheckOutData }) {
  const { t } = useTranslation(['tasks']);

  return (
    <div className="task-detail-section card">
      <h3 className="task-section-label">{t('tasks:notesLabel')}</h3>
      <textarea
        className="task-notes-input"
        rows="6"
        value={checkOutData.summary}
        onChange={(e) => setCheckOutData({ ...checkOutData, summary: e.target.value })}
        placeholder={t('tasks:notesPlaceholder')}
      />
    </div>
  );
}

function SessionEndFields({
  checkInTime,
  checkIns,
  userId,
  timeSpent,
  endTimeLocal,
  onSessionChange,
  formatTime,
  formatDateTime,
  idPrefix = 'task-session'
}) {
  const { t } = useTranslation(['tasks']);
  const priorMinutes = cumulativeWorkedMinutes(checkIns, userId);
  const endInputId = `${idPrefix}-end`;
  const minutesInputId = `${idPrefix}-minutes`;
  const maxMinutes = checkInTime ? currentSessionMinutes(checkInTime) : 0;

  const handleMinutesChange = (value) => {
    const parsed = Number.parseInt(value, 10);
    let nextMinutes = value;
    let end = sessionEndFromMinutes(checkInTime, value);
    if (!Number.isNaN(parsed) && maxMinutes > 0 && parsed > maxMinutes) {
      nextMinutes = String(maxMinutes);
      end = sessionEndFromMinutes(checkInTime, maxMinutes);
    }
    onSessionChange({
      timeSpent: nextMinutes,
      endTimeLocal: end ? toDateTimeLocalValue(end) : endTimeLocal,
      manual: true
    });
  };

  const handleEndChange = (value) => {
    const endIso = fromDateTimeLocalValue(value);
    const minutes = endIso && checkInTime
      ? currentSessionMinutes(checkInTime, endIso)
      : 0;
    onSessionChange({
      timeSpent: minutes > 0 ? String(minutes) : '',
      endTimeLocal: value,
      manual: true
    });
  };

  return (
    <>
      <p className="task-time-display">
        {t('tasks:checkInTimeReadonly', {
          datetime: checkInTime ? formatDateTime(checkInTime) : '—'
        })}
      </p>
      {priorMinutes > 0 && (
        <p className="task-time-display">
          {t('tasks:priorSegmentsWorked', {
            time: formatTime(priorMinutes)
          })}
        </p>
      )}
      <p className="task-pause-checkout-note">{t('tasks:sessionEndHelp')}</p>
      <label className="task-session-field-label" htmlFor={endInputId}>
        {t('tasks:sessionEndTimeLabel')}
      </label>
      <input
        id={endInputId}
        type="datetime-local"
        className="task-time-input"
        value={endTimeLocal}
        max={toDateTimeLocalValue(new Date())}
        onChange={(e) => handleEndChange(e.target.value)}
        aria-label={t('tasks:sessionEndTimeLabel')}
      />
      <label className="task-session-field-label" htmlFor={minutesInputId}>
        {t('tasks:durationMinutesLabel')}
      </label>
      <input
        id={minutesInputId}
        type="number"
        className="task-time-input"
        value={timeSpent}
        onChange={(e) => handleMinutesChange(e.target.value)}
        min="1"
        max={maxMinutes > 0 ? maxMinutes : undefined}
        step="1"
        placeholder={t('tasks:durationMinutes')}
        aria-label={t('tasks:durationMinutesLabel')}
      />
      {timeSpent && (
        <p className="task-time-display">{formatTime(parseInt(timeSpent, 10))}</p>
      )}
    </>
  );
}

/** Compact read-only visit total; editing happens in checkout / done dialogs. */
export function TaskTimeSummaryCard({
  checkInTime,
  checkIns,
  userId,
  formatTime
}) {
  const { t } = useTranslation(['tasks']);
  const visitTotal = totalWorkedMinutes(checkIns, userId, checkInTime, 0);

  return (
    <div className="task-detail-section card task-time-summary">
      <h3 className="task-section-label">{t('tasks:timeSection')}</h3>
      <p className="task-time-display task-time-total">
        {t('tasks:totalWorkedIncludingEarlier', {
          time: formatTime(Number.isFinite(visitTotal) ? visitTotal : 0)
        })}
      </p>
      <p className="task-session-idle-hint">{t('tasks:editTimeOnCheckoutHint')}</p>
    </div>
  );
}

export function TaskActionsCard({
  canCheckIn,
  canCheckOut,
  canMarkDoneWithoutCheckIn,
  onCheckIn,
  onMarkDoneWithoutCheckIn,
  onOpenPauseModal,
  onOpenDoneModal,
  markDoneSubmitting = false
}) {
  const { t } = useTranslation(['tasks', 'common']);

  return (
    <div className="task-detail-section card">
      <h3 className="task-section-label">{t('common:actions')}</h3>
      <div className="task-actions-buttons">
        {canCheckIn && (
          <button type="button" className="btn btn-primary task-check-button" onClick={onCheckIn}>
            {t('tasks:checkIn')}
          </button>
        )}
        {canMarkDoneWithoutCheckIn && (
          <button
            type="button"
            className="btn btn-secondary task-check-button"
            onClick={onMarkDoneWithoutCheckIn}
            disabled={markDoneSubmitting}
          >
            {markDoneSubmitting ? t('tasks:markingTaskDone') : t('tasks:markTaskDone')}
          </button>
        )}
        {canCheckOut && (
          <div className="task-check-actions">
            <button
              type="button"
              className="btn btn-secondary task-check-button"
              onClick={onOpenPauseModal}
            >
              {t('tasks:checkOut')}
            </button>
            <button
              type="button"
              className="btn btn-primary task-check-button"
              onClick={onOpenDoneModal}
              disabled={markDoneSubmitting}
            >
              {markDoneSubmitting ? t('tasks:markingTaskDone') : t('tasks:markTaskDone')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export function TaskCompletedCard() {
  const { t } = useTranslation(['tasks']);

  return (
    <div className="task-detail-section card task-completed-message">
      <p>{t('tasks:taskCompletedMessage')}</p>
    </div>
  );
}

export function TaskSessionEndModal({
  mode,
  isOpen,
  onClose,
  form,
  setForm,
  submitting,
  onSubmit,
  checkInTime,
  checkIns,
  userId,
  formatTime,
  formatDateTime,
  extraContent = null
}) {
  const { t } = useTranslation(['tasks', 'common']);
  const isDone = mode === 'done';
  const lunchMinutes = parsePauseLunchMinutes(form.took_lunch, form.lunch_minutes);
  const parsedSession = Number.parseInt(form.timeSpent, 10);
  const sessionGross = Number.isFinite(parsedSession)
    ? parsedSession
    : currentSessionMinutes(checkInTime);
  const sessionNet = Math.max(sessionGross - lunchMinutes, 0);
  const visitTotal = totalWorkedMinutes(checkIns, userId, checkInTime, lunchMinutes);
  const idPrefix = isDone ? 'task-done-session' : 'task-pause-session';
  const lunchInputId = isDone ? 'task-done-lunch-minutes' : 'task-pause-lunch-minutes';

  const formId = `${idPrefix}-form`;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isDone ? t('tasks:doneCheckoutModalTitle') : t('tasks:pauseCheckoutModalTitle')}
      className="task-pause-checkout-modal"
    >
      <form id={formId} className="task-pause-checkout-form" onSubmit={onSubmit}>
        <p className="task-pause-checkout-help">
          {isDone ? t('tasks:doneCheckoutHelp') : t('tasks:pauseCheckoutHelp')}
        </p>
        <SessionEndFields
          idPrefix={idPrefix}
          checkInTime={checkInTime}
          checkIns={checkIns}
          userId={userId}
          timeSpent={form.timeSpent}
          endTimeLocal={form.endTimeLocal}
          formatTime={formatTime}
          formatDateTime={formatDateTime}
          onSessionChange={({ timeSpent, endTimeLocal, manual }) => {
            setForm((prev) => ({
              ...prev,
              timeSpent,
              endTimeLocal,
              manual: manual || prev.manual
            }));
          }}
        />
        <label className="task-pause-checkbox">
          <input
            type="checkbox"
            checked={!!form.took_lunch}
            onChange={(event) => {
              const checked = event.target.checked;
              setForm((prev) => ({
                ...prev,
                took_lunch: checked,
                lunch_minutes: checked ? (prev.lunch_minutes || '30') : ''
              }));
            }}
          />
          {t('tasks:tookLunchBreak')}
        </label>
        {form.took_lunch && (
          <>
            <label htmlFor={lunchInputId}>{t('tasks:lunchDurationMinutes')}</label>
            <input
              id={lunchInputId}
              type="number"
              min="1"
              step="1"
              value={form.lunch_minutes}
              onChange={(event) => setForm((prev) => ({
                ...prev,
                lunch_minutes: event.target.value
              }))}
              required
            />
          </>
        )}
        {lunchMinutes > 0 && (
          <p className="task-time-display">
            {t('tasks:sessionNetAfterLunch', {
              time: formatTime(sessionNet),
              lunch: lunchMinutes
            })}
          </p>
        )}
        <p className="task-time-display task-time-total" aria-live="polite">
          {t('tasks:totalWorkedIncludingEarlier', {
            time: formatTime(Number.isFinite(visitTotal) ? visitTotal : 0)
          })}
        </p>
        <p className="task-pause-checkout-note">
          {isDone ? t('tasks:doneCheckoutLunchHint') : t('tasks:pauseCheckoutLunchHint')}
        </p>
      </form>
      {/* Outside session form so article add cannot nest/submit checkout. */}
      {isDone && extraContent}
      <div className="modal-actions">
        <button
          type="button"
          className="btn btn-secondary"
          onClick={onClose}
          disabled={submitting}
        >
          {t('common:cancel')}
        </button>
        <button type="submit" form={formId} className="btn btn-primary" disabled={submitting}>
          {submitting
            ? (isDone ? t('tasks:markingTaskDone') : t('tasks:savingPauseCheckout'))
            : (isDone ? t('tasks:markTaskDone') : t('tasks:checkOut'))}
        </button>
      </div>
    </Modal>
  );
}
